/**
 * TourTooltip — the editorial card that floats next to the spotlit target.
 *
 * Layout: small uppercased eyebrow ("Step N of M") in mono-ish letterform,
 * a serif heading (Tiempos), 1–2 sentences of sans body, a step indicator
 * (•••○○), and two buttons. The last step's primary button label flips to
 * "Got it" — handled by the caller via `isLastStep`.
 *
 * Placement algorithm: start with `placement` (default 'auto'). Compute the
 * preferred position; if it would overflow the viewport, walk through
 * fallbacks in order [bottom, top, right, left]. If everything overflows
 * (small viewport), pin to the visible region.
 *
 * Visuals: surface = `--wayne-surface`, hairline border, soft drop-shadow,
 * pill buttons (primary green, secondary outlined). All colors come from
 * CSS vars so dark mode flips automatically.
 */
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { TourPlacement, TourRect } from './types';

const TOOLTIP_WIDTH = 400;
// Sized to comfortably fit eyebrow + heading + 2-sentence body + step
// indicator + buttons. The card height is dominated by content; this is a
// conservative estimate used only by the flip math when measuring isn't
// possible yet (first paint).
const TOOLTIP_HEIGHT_ESTIMATE = 240;
const GAP = 12;

export interface TourTooltipProps {
  readonly eyebrow: string;
  readonly title: ReactNode;
  readonly body: ReactNode;
  readonly currentStep: number;
  readonly totalSteps: number;
  readonly isLastStep: boolean;
  readonly canGoBack: boolean;
  readonly placement: TourPlacement;
  readonly targetRect: TourRect | null;
  readonly reducedMotion: boolean;
  readonly onSkip: () => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly titleId: string;
  readonly descriptionId: string;
}

interface ResolvedPosition {
  readonly top: number;
  readonly left: number;
  readonly placement: Exclude<TourPlacement, 'auto'>;
}

function fitsInViewport(
  pos: { top: number; left: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
): boolean {
  return (
    pos.top >= 0 &&
    pos.left >= 0 &&
    pos.top + size.height <= viewport.height &&
    pos.left + size.width <= viewport.width
  );
}

function computePlacement(
  rect: TourRect | null,
  preferred: TourPlacement,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
): ResolvedPosition {
  // No measured target yet — center the tooltip so the user sees something
  // sensible during the first paint frame.
  if (!rect) {
    return {
      top: Math.max(0, viewport.height / 2 - size.height / 2),
      left: Math.max(0, viewport.width / 2 - size.width / 2),
      placement: 'bottom',
    };
  }

  const order: ReadonlyArray<Exclude<TourPlacement, 'auto'>> =
    preferred === 'auto'
      ? ['bottom', 'top', 'right', 'left']
      : [
          preferred,
          ...(['bottom', 'top', 'right', 'left'] as const).filter(
            (p) => p !== preferred,
          ),
        ];

  for (const p of order) {
    const pos = positionFor(rect, p, size);
    if (fitsInViewport(pos, size, viewport)) {
      return { ...pos, placement: p };
    }
  }

  // Nothing fit — pin into the visible region using the first option.
  const fallback = order[0] ?? 'bottom';
  const pos = positionFor(rect, fallback, size);
  const clampedTop = Math.max(
    0,
    Math.min(pos.top, viewport.height - size.height),
  );
  const clampedLeft = Math.max(
    0,
    Math.min(pos.left, viewport.width - size.width),
  );
  return { top: clampedTop, left: clampedLeft, placement: fallback };
}

function positionFor(
  rect: TourRect,
  placement: Exclude<TourPlacement, 'auto'>,
  size: { width: number; height: number },
): { top: number; left: number } {
  switch (placement) {
    case 'top':
      return {
        top: rect.top - size.height - GAP,
        left: rect.left + rect.width / 2 - size.width / 2,
      };
    case 'bottom':
      return {
        top: rect.top + rect.height + GAP,
        left: rect.left + rect.width / 2 - size.width / 2,
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2 - size.height / 2,
        left: rect.left - size.width - GAP,
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2 - size.height / 2,
        left: rect.left + rect.width + GAP,
      };
  }
}

/** Step indicator: filled circles for visited steps, hairline for upcoming. */
function StepIndicator({
  total,
  current,
}: {
  total: number;
  current: number;
}): React.ReactElement {
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current + 1}
      aria-label={`Step ${current + 1} of ${total}`}
      className="flex items-center gap-1.5"
    >
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`block w-2 h-2 rounded-full border ${
            i <= current
              ? 'bg-wayne-accent border-wayne-accent'
              : 'bg-transparent border-wayne-hairline-2'
          }`}
        />
      ))}
    </div>
  );
}

const TourTooltip = forwardRef<HTMLDivElement, TourTooltipProps>(
  function TourTooltip(props, forwardedRef) {
    const {
      eyebrow,
      title,
      body,
      currentStep,
      totalSteps,
      isLastStep,
      canGoBack,
      placement,
      targetRect,
      reducedMotion,
      onSkip,
      onBack,
      onNext,
      titleId,
      descriptionId,
    } = props;

    const internalRef = useRef<HTMLDivElement | null>(null);
    const nextButtonRef = useRef<HTMLButtonElement | null>(null);
    const [measured, setMeasured] = useState<{
      width: number;
      height: number;
    } | null>(null);

    // Measure once content is in the DOM so the placement math uses the real
    // height, not the estimate. Re-measures on step change because body
    // length varies.
    useEffect(() => {
      const el = internalRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setMeasured({ width: rect.width, height: rect.height });
      }
    }, [currentStep, title, body]);

    // Move keyboard focus into the tour's primary action when the step
    // changes. Using a ref + effect (instead of `autoFocus`) keeps
    // `jsx-a11y/no-autofocus` happy while preserving keyboard parity.
    useEffect(() => {
      nextButtonRef.current?.focus();
    }, [currentStep]);

    const viewport = useMemo(
      () => ({
        width: typeof window === 'undefined' ? 1024 : window.innerWidth,
        height: typeof window === 'undefined' ? 768 : window.innerHeight,
      }),
      // Recompute when the rect changes — that's the trigger we have for
      // "user resized / scrolled / moved to new step".
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [targetRect],
    );

    const size = useMemo(
      () =>
        measured ?? {
          width: TOOLTIP_WIDTH,
          height: TOOLTIP_HEIGHT_ESTIMATE,
        },
      [measured],
    );

    const resolved = useMemo(
      () => computePlacement(targetRect, placement, size, viewport),
      [targetRect, placement, size, viewport],
    );

    const setRefs = (node: HTMLDivElement | null): void => {
      internalRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    };

    const enterAnimClass = reducedMotion
      ? ''
      : 'tour-tooltip-enter';

    return (
      <div
        ref={setRefs}
        data-testid="tour-tooltip"
        data-placement={resolved.placement}
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        className={`fixed z-[71] w-[400px] max-w-[calc(100vw-32px)] bg-wayne-surface border border-wayne-hairline rounded-xl shadow-[0_18px_48px_-12px_rgba(15,19,17,0.35),0_4px_12px_-4px_rgba(15,19,17,0.18)] px-6 pt-6 pb-5 focus:outline-none ${enterAnimClass}`}
        style={{
          top: `${resolved.top}px`,
          left: `${resolved.left}px`,
        }}
      >
        <div
          className="text-[10.5px] font-bold uppercase text-wayne-ink-3 mb-2"
          style={{ letterSpacing: '0.18em' }}
        >
          {eyebrow}
        </div>
        <h2
          id={titleId}
          className="font-serif text-wayne-ink m-0 mb-2"
          style={{
            fontFamily:
              "'Tiempos Headline', 'Source Serif Pro', 'Iowan Old Style', Georgia, serif",
            fontSize: '22px',
            lineHeight: 1.2,
            fontWeight: 500,
            letterSpacing: '-0.012em',
          }}
        >
          {title}
        </h2>
        <p
          id={descriptionId}
          className="text-[13.5px] text-wayne-ink-2 m-0 mb-5"
          style={{ lineHeight: 1.6 }}
        >
          {body}
        </p>
        <div className="flex items-center gap-3">
          <StepIndicator total={totalSteps} current={currentStep} />
          <div className="flex-1" />
          <button
            type="button"
            onClick={onSkip}
            className="bg-transparent border-0 text-wayne-ink-3 hover:text-wayne-ink-2 px-2 h-9 text-[12.5px] font-semibold transition-colors"
          >
            Skip
          </button>
          {canGoBack ? (
            <button
              type="button"
              onClick={onBack}
              className="bg-transparent border border-wayne-hairline-2 text-wayne-ink-2 hover:text-wayne-ink rounded-full px-3.5 h-9 text-[12.5px] font-semibold transition-colors"
            >
              Back
            </button>
          ) : null}
          <button
            ref={nextButtonRef}
            type="button"
            onClick={onNext}
            className="bg-wayne-accent hover:bg-wayne-accent-2 rounded-full px-5 h-9 text-[12.5px] font-bold transition-colors"
            style={{ color: 'var(--wayne-bg)' }}
          >
            {isLastStep ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    );
  },
);

export default TourTooltip;
